import { Injectable } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidatorFn } from '@angular/forms';

import { DecodeVinResponse } from '@dav-model/api/response/decode-vin.response';
import { Country } from '@dav-model/countryCode';
import { Field } from '@dav-model/form/field';

import { ValidationService } from './validation.service';
import * as fromValidations from '../validations/forms/generic.validation';

@Injectable({
  providedIn: 'root',
})
export class FormService {
  constructor(private formBuilder: UntypedFormBuilder) {
  }

  triggerValidityOfAllFields(form: UntypedFormGroup) {
    Object.keys(form.controls).forEach(field => {
      const control = form.get(field);
      control.markAsTouched({ onlySelf: true });
    });
  }

  // CONVERT FIELD ARRAY TO FORM GROUP
  setForm(response: Field[]) {
    const form: UntypedFormGroup = this.formBuilder.group({});
    response.forEach((element) => {
      if (element.display) {
        const field = this.setDefault(element);
        const validations = this.getValidations(element);
        field.setValidators(validations);
        form.addControl(element.name, field);
      }
    });
    ValidationService.BusinessFieldValidate(form);

    return form;
  }

  setProductFormAfterVinDecodedSuccessfully(productForm: UntypedFormGroup, vinData: DecodeVinResponse) {
    productForm.get('vin').setValue(vinData.vin);
    productForm.get('year').setValue(vinData.year[0]);
    productForm.get('make').setValue(vinData.make[0]);
    productForm.get('model').setValue(vinData.model[0]);

    if (productForm.get('inServiceDate')) {
      productForm.get('inServiceDate').enable();
    }

    productForm.get('vin').disable();
    // set inservice date
    if (vinData.inserviceDateFound) {
      const insDate = new Date(vinData.inserviceDate);
      const insDateObj = {
        year: insDate.getFullYear(),
        month: insDate.getMonth() + 1,
        day: insDate.getDate(),
      };
      if (productForm.get('inServiceDate')) {
        productForm.get('inServiceDate').patchValue({ date: insDateObj, formatted: vinData.inserviceDate });
        productForm.get('inServiceDate').disable();
      }

    } else {
      if (productForm.get('inServiceDate')) {
        productForm.get('inServiceDate').patchValue(null);
      }
    }
  }

  setProductFormForManualDecode(productForm: UntypedFormGroup) {
    if (productForm) {
      // don't overwrite existing values in case we're loading a stored form
      if (!productForm.get('year').value) {
        productForm.get('year').setValue(null);
      }
      if (!productForm.get('make').value) {
        productForm.get('make').setValue(null);
      }
      if (!productForm.get('model').value) {
        productForm.get('model').setValue(null);
      }

      if (productForm.get('inServiceDate')) {
        productForm.get('inServiceDate').enable();
      }
    }
  }

  private setDefault(element: Field): UntypedFormControl {
    const field = new UntypedFormControl();
    field['label'] = element.label;
    field.setValue(element.default);
    if (element.readOnly) {
      field.disable();
    }
    // only for tax fields
    if (element['taxPercentage']) {
      field['taxPercentage'] = element['taxPercentage'] / 100.0;
    }

    return field;
  }

  private getValidations(element: Field): ValidatorFn[] {
    let validations: ValidatorFn[] = [];
    if (element.required) {
      validations.push(fromValidations.RequiredValidator);
    }
    element.validations.forEach(elementValidation => {
      // running all validations from validation.service with switch statement
      validations = ValidationService.addValidation(validations, elementValidation);
    });

    return validations;
  }

  getFormattedPostalCode(postalCode: string): string {
    let formatedValue = '';
    if (postalCode === null) {
      return '';
    }

    if (postalCode.length > 5 && !postalCode.includes(' ')) {
      formatedValue = `${postalCode.slice(0, 3)} ${postalCode.slice(3)}`;
    } else if (postalCode.length <= 5) {
      formatedValue = postalCode.replace(' ', '');
    } else {
      formatedValue = postalCode;
    }

    return formatedValue.toUpperCase();
  }

  setCountryCode(postalCode: string): string {

    if (postalCode === null) {
      return '';
    }
    const postalCodeLength = postalCode.length;

    if (postalCodeLength === Country.USAPostal) {
      return Country.USA;
    }
    if (postalCodeLength === Country.CANPostalWS || postalCodeLength === Country.CANPostalWIS) {
      return Country.CAN;
    }

    return '';
  }
}


